import { useEffect, useState } from "react";
import { CalendarDays, Clock, MapPin } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { fileUrl } from "@/lib/api";
import { PageHero, Reveal, EmptyState, formatDate } from "@/components/public/Shared";

function EventCard({ ev }) {
  return (
    <article className="flex h-full flex-col rounded-lg border border-border bg-white p-7 shadow-sm transition-transform duration-300 hover:-translate-y-1" data-testid={`event-card-${ev.id}`}>
      {ev.image_url && (
        <img src={fileUrl(ev.image_url)} alt={ev.title} className="mb-5 h-44 w-full rounded-md object-cover" loading="lazy" />
      )}
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold-dark">
        <CalendarDays className="h-4 w-4" /> {formatDate(ev.date)}
      </p>
      <h3 className="mt-3 font-heading text-2xl font-bold text-navy">{ev.title}</h3>
      <div className="mt-3 space-y-1.5 text-sm text-foreground/75">
        {(ev.start_time || ev.end_time) && (
          <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> {ev.start_time}{ev.end_time ? ` – ${ev.end_time}` : ""}</p>
        )}
        {ev.location && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" /> {ev.location}</p>}
      </div>
      {ev.description && <p className="mt-4 text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{ev.description}</p>}
      {ev.category && <span className="mt-5 inline-block w-fit rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-navy/70">{ev.category}</span>}
    </article>
  );
}

export default function Events() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/events").then((r) => setData(r.data)).catch(() => setData({ upcoming: [], archive: [] }));
  }, []);

  return (
    <div data-testid="events-page">
      <PageHero title="Church Events" subtitle="Special services, celebrations and gatherings in the life of our church." />
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <Tabs defaultValue="upcoming">
          <TabsList className="mb-10 bg-secondary" data-testid="events-tabs">
            <TabsTrigger value="upcoming" data-testid="events-tab-upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="archive" data-testid="events-tab-archive">Past Events</TabsTrigger>
          </TabsList>
          <TabsContent value="upcoming">
            {data && data.upcoming.length === 0 && (
              <EmptyState testId="events-upcoming-empty" title="No upcoming events" text="New events will be announced here soon. Please check back." />
            )}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {(data?.upcoming || []).map((ev, i) => (
                <Reveal key={ev.id} delay={i * 0.07}><EventCard ev={ev} /></Reveal>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="archive">
            {data && data.archive.length === 0 && (
              <EmptyState testId="events-archive-empty" title="No past events yet" text="Completed events will be archived here." />
            )}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {(data?.archive || []).map((ev, i) => (
                <Reveal key={ev.id} delay={i * 0.07}><EventCard ev={ev} /></Reveal>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
